import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Flex,
  Icon,
} from '@kognitos/lattice';
import { IntegrationAssistantPanel } from '@/modules/integrations/components/IntegrationAssistantPanel';
import {
  TriggerAssistantDialogLayout,
  TRIGGER_DIALOG_MAX_WIDTH_CLASS,
} from '../shared/TriggerAssistantDialogLayout';
import { useFillHighlight } from '../shared/useFillHighlight';
import { useTriggerAssistant } from '../shared/useTriggerAssistant';
import { TriggerStatusBadge } from '../TriggerTypeSelector/TriggerStatusBadge';
import { DialogStepper } from './DialogStepper';
import { ConfigureStep } from './ConfigureStep';
import { MapInputsStep } from './MapInputsStep';
import type { IWebhookTriggerDialogProps, IStepConfig } from './types';
import type { V2BookTriggerDescriptor } from '@/generated/hooks/bdk/models';
import {
  getPrimaryButtonLabel,
  getPrimaryButtonVariant,
  getDialogTitle,
  getMissingRequiredConfigFields,
} from './utils';
import {
  convertConditionsToExpression,
  extractFilterableFields,
  getInvalidFilterConditionIds,
  parseFilterOperator,
  type IFilterCondition,
} from './filter-utils';
import { TRIGGER_STATUS } from '../TriggerTypeSelector/TriggerType';
import {
  getConnectionAction,
  useInactiveConnectionGate,
} from '../../hooks/use-inactive-connection-gate';

/** Error returned to shifu when it passes a filter operator we don't recognize. */
function unknownOperatorMessage(operator: string): string {
  return `Unknown operator "${operator}". Use one of: equals, not_equals, contains, not_contains.`;
}

/**
 * WebhookTriggerDialog Component
 *
 * A multi-step dialog for configuring webhook triggers.
 * Supports both 'create' mode for new triggers and 'edit' mode for existing triggers.
 *
 * Contains two steps:
 * 1. Configure - Select integration, connection, and event
 * 2. Map Inputs - Map webhook payload to automation inputs
 *
 * In edit mode:
 * - Pre-fills form with existing trigger data
 * - Shows dynamic button labels based on whether changes were made
 * - Displays "Pause Trigger" button in footer
 * - Shows Active/Paused badge in header
 *
 * @param props - Component props
 * @returns JSX element containing the dialog
 */
export function WebhookTriggerDialog({
  isOpen,
  onOpenChange,
  inputSpecs,
  isLoading = false,
  onSave,
  mode = 'create',
  initialData,
  triggerName,
  triggerStatus,
  onUpdate,
  onToggleActiveState,
  isToggleActiveStateLoading,
  onDelete,
  // Controlled state props
  currentStep,
  onStepChange,
  selectedIntegration,
  onSelectedIntegrationChange,
  selectedIntegrationVersion,
  onSelectedIntegrationVersionChange,
  selectedConnection,
  onSelectedConnectionChange,
  selectedEvent,
  onSelectedEventChange,
  inputMappings,
  onInputMappingsChange,
  triggerFieldGroups,
  onTriggerFieldGroupsChange,
  configurationValues,
  onConfigurationValuesChange,
  filterConditions,
  onFilterConditionsChange,
}: IWebhookTriggerDialogProps) {
  const { t } = useTranslation('automation-inputs');

  // Shared assistant wiring (gate + room + canAssist, org/workspace ids, the
  // integration display-name state, and the config-form highlight). The webhook
  // dialog additionally owns a second highlight (`filterFill`) for its filters.
  const {
    canAssist,
    organizationId,
    workspaceId,
    selectedIntegrationDisplayName,
    setSelectedIntegrationDisplayName,
    configFill,
  } = useTriggerAssistant(
    selectedIntegration,
    selectedIntegrationVersion,
    selectedConnection,
    selectedEvent,
  );

  // Check if inputSpecs has any items
  const hasInputSpecs = useMemo(
    () => inputSpecs && inputSpecs.length > 0,
    [inputSpecs],
  );

  /**
   * Checks if configure step values have changed from initial values
   */
  const hasConfigureChanges = useMemo(() => {
    if (mode === 'create') return false;

    return (
      selectedIntegration !== initialData?.selectedIntegration ||
      selectedIntegrationVersion !== initialData?.selectedIntegrationVersion ||
      selectedConnection !== initialData?.selectedConnection ||
      selectedEvent !== initialData?.selectedEvent
    );
  }, [
    mode,
    selectedIntegration,
    selectedIntegrationVersion,
    selectedConnection,
    selectedEvent,
    initialData,
  ]);

  /**
   * Checks if mapping values have changed from initial values
   */
  const hasMappingChanges = useMemo(() => {
    if (mode === 'create') return false;

    const initialMappings = initialData?.inputMappings ?? {};
    const currentKeys = Object.keys(inputMappings);
    const initialKeys = Object.keys(initialMappings);

    // Check if number of mappings is different
    if (currentKeys.length !== initialKeys.length) return true;

    // Check if any mapping value is different
    return currentKeys.some(key => inputMappings[key] !== initialMappings[key]);
  }, [mode, inputMappings, initialData]);

  /**
   * Checks if configuration values have changed from initial values
   */
  const hasConfigurationChanges = useMemo(() => {
    if (mode === 'create') return false;

    const initialConfig = initialData?.configurationValues ?? {};
    const currentKeys = Object.keys(configurationValues);
    const initialKeys = Object.keys(initialConfig);

    // Check if number of configuration keys is different
    if (currentKeys.length !== initialKeys.length) return true;

    // Check if any configuration value is different
    return currentKeys.some(
      key => configurationValues[key] !== initialConfig[key],
    );
  }, [mode, configurationValues, initialData]);

  /**
   * Checks if filter conditions have changed from initial values.
   *
   * Compares the serialized BDK expressions rather than the raw condition
   * arrays so that client-only `id`s, ordering noise, and incomplete rows don't
   * register as changes.
   */
  const hasFilterChanges = useMemo(() => {
    if (mode === 'create') return false;

    const current = JSON.stringify(
      convertConditionsToExpression(filterConditions) ?? null,
    );
    const initial = JSON.stringify(
      convertConditionsToExpression(initialData?.filterConditions ?? []) ??
        null,
    );
    return current !== initial;
  }, [mode, filterConditions, initialData]);

  /**
   * Checks if any changes have been made across all steps
   */
  const hasAnyChanges = useMemo(() => {
    return (
      hasConfigureChanges ||
      hasMappingChanges ||
      hasConfigurationChanges ||
      hasFilterChanges
    );
  }, [
    hasConfigureChanges,
    hasMappingChanges,
    hasConfigurationChanges,
    hasFilterChanges,
  ]);

  /**
   * Handles mapping change for an input
   */
  const handleMappingChange = useCallback(
    (inputName: string, triggerFieldValue: string) => {
      onInputMappingsChange({
        ...inputMappings,
        [inputName]: triggerFieldValue,
      });
    },
    [inputMappings, onInputMappingsChange],
  );

  // Per-field validation errors for the configuration form. Populated on Save
  // when a required field is empty; cleared per-key as the user edits.
  const [configErrors, setConfigErrors] = useState<Record<string, string>>({});

  // Per-condition validation errors for the filter builder, keyed by condition
  // id. Populated on Save when a value-requiring operator has an empty value.
  const [filterErrors, setFilterErrors] = useState<Record<string, string>>({});

  // Track the resolved trigger descriptor so we can validate against its schema.
  const [selectedTriggerDescriptor, setSelectedTriggerDescriptor] = useState<
    V2BookTriggerDescriptor | undefined
  >();

  /**
   * Handles configuration value change
   */
  const handleConfigurationChange = useCallback(
    (key: string, value: string | boolean | number) => {
      onConfigurationValuesChange({
        ...configurationValues,
        [key]: value,
      });
      // Clear any standing error for this field as soon as the user edits it,
      // so the inline message disappears without waiting for another Save click.
      setConfigErrors(prev => {
        if (!(key in prev)) return prev;
        const { [key]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [configurationValues, onConfigurationValuesChange],
  );

  /**
   * Handles filter condition changes. Forwards to the parent and drops any
   * standing error for a condition once it becomes valid (or is removed), so
   * inline messages clear as the user fixes them.
   */
  const handleFilterConditionsChange = useCallback(
    (conditions: typeof filterConditions) => {
      onFilterConditionsChange(conditions);
      setFilterErrors(prev => {
        if (Object.keys(prev).length === 0) return prev;
        const stillInvalid = new Set(getInvalidFilterConditionIds(conditions));
        const next = Object.fromEntries(
          Object.entries(prev).filter(([id]) => stillInvalid.has(id)),
        );
        return Object.keys(next).length === Object.keys(prev).length
          ? prev
          : next;
      });
    },
    [onFilterConditionsChange],
  );

  // Mirror the latest filter conditions so shifu's client-tool handlers always
  // read and extend the current array. Several calls can settle in the same tick
  // (shifu fires add/remove back-to-back); reading the render-time closure would
  // let the second overwrite the first. `commitFilterConditions` updates this
  // ref synchronously so same-tick calls compound correctly, and the effect
  // re-syncs it after user edits coming through props.
  const filterConditionsRef = useRef(filterConditions);
  // eslint-disable-next-line no-restricted-syntax -- reason: mirror latest conditions for the async client-tool handlers
  useEffect(() => {
    filterConditionsRef.current = filterConditions;
  }, [filterConditions]);

  // The config-form highlight comes from the shared hook; the filters section is
  // webhook-only, so its highlight stays local here.
  const filterFill = useFillHighlight();

  /**
   * Fills a configuration field on shifu's behalf (its `set_trigger_field`
   * client tool). Validates the key against the trigger's own configuration
   * schema so a hallucinated or injected key can't write an arbitrary (or
   * framework-managed) entry into the saved configuration; returns an error
   * string (surfaced back to shifu) when the key is unknown.
   */
  const handleAssistantFillForm = useCallback(
    (field: string, value: string): string | void => {
      const validKeys = Object.keys(
        selectedTriggerDescriptor?.configuration ?? {},
      );
      if (!validKeys.includes(field)) {
        return validKeys.length
          ? `Unknown field "${field}". Fillable fields: ${validKeys.join(', ')}.`
          : 'This trigger has no fillable configuration fields.';
      }
      handleConfigurationChange(field, value);
      configFill.mark(field);
    },
    [selectedTriggerDescriptor, handleConfigurationChange, configFill],
  );

  /**
   * Resolves a shifu-supplied field name or label to a canonical filterable
   * field. Both add and remove go through this so a filter added by its label
   * can also be removed by name (and vice versa).
   */
  const resolveFilterField = useCallback(
    (field: string) => {
      const normalized = field.trim().toLowerCase();
      return extractFilterableFields(selectedTriggerDescriptor).find(
        f =>
          f.name.toLowerCase() === normalized ||
          f.label.toLowerCase() === normalized,
      );
    },
    [selectedTriggerDescriptor],
  );

  /**
   * Commits the next filter conditions: updates the mirror ref synchronously (so
   * back-to-back client-tool calls compound) then forwards to the shared change
   * handler.
   */
  const commitFilterConditions = useCallback(
    (next: IFilterCondition[]) => {
      filterConditionsRef.current = next;
      handleFilterConditionsChange(next);
    },
    [handleFilterConditionsChange],
  );

  /**
   * Adds a filter condition on shifu's behalf (its `add_trigger_filter` client
   * tool). Validates the field and operator; returns an error string (surfaced
   * back to shifu) when either is unknown, so it can correct itself.
   */
  const handleAddFilter = useCallback(
    (field: string, operator: string, value: string): string | void => {
      const fields = extractFilterableFields(selectedTriggerDescriptor);
      if (fields.length === 0) {
        return 'This trigger has no filterable fields.';
      }
      const match = resolveFilterField(field);
      if (!match) {
        return `Unknown field "${field}". Filterable fields: ${fields
          .map(f => f.name)
          .join(', ')}.`;
      }
      const op = parseFilterOperator(operator);
      if (!op) {
        return unknownOperatorMessage(operator);
      }
      // Dedup: if an identical condition already exists, don't add it again.
      // Tell shifu so it doesn't keep retrying (it can't see the form state).
      const current = filterConditionsRef.current;
      const alreadyExists = current.some(
        c =>
          c.field === match.name &&
          c.operator === op &&
          String(c.value ?? '') === String(value ?? ''),
      );
      if (alreadyExists) {
        return `That filter (${match.name} ${op} ${value}) is already added — no need to add it again.`;
      }
      const id = crypto.randomUUID();
      commitFilterConditions([
        ...current,
        { id, field: match.name, operator: op, value },
      ]);
      filterFill.mark(id);
    },
    [
      selectedTriggerDescriptor,
      resolveFilterField,
      commitFilterConditions,
      filterFill,
    ],
  );

  /**
   * Removes a filter condition on shifu's behalf (`remove_trigger_filter`).
   * Resolves the field through the same lookup as add (name or label), then
   * matches by field / operator / value; returns an error string (surfaced to
   * shifu) when the operator is unknown or no matching condition exists.
   */
  const handleRemoveFilter = useCallback(
    (field: string, operator: string, value: string): string | void => {
      const op = parseFilterOperator(operator);
      if (!op) {
        return unknownOperatorMessage(operator);
      }
      // Fall back to the raw field when it isn't in the current schema — a
      // matching condition may still exist (e.g. the event changed).
      const targetField = (
        resolveFilterField(field)?.name ?? field
      ).toLowerCase();
      const current = filterConditionsRef.current;
      const idx = current.findIndex(
        c =>
          c.field.toLowerCase() === targetField &&
          c.operator === op &&
          String(c.value ?? '') === String(value ?? ''),
      );
      if (idx === -1) {
        return `No matching filter (${field} ${op} ${value}) to remove.`;
      }
      commitFilterConditions(current.filter((_, i) => i !== idx));
    },
    [resolveFilterField, commitFilterConditions],
  );

  /** Removes all filter conditions on shifu's behalf (`clear_trigger_filters`). */
  const handleClearFilters = useCallback(() => {
    commitFilterConditions([]);
  }, [commitFilterConditions]);

  // Step configuration - only show step 2 if there are inputSpecs
  const steps: IStepConfig[] = useMemo(() => {
    const baseSteps: IStepConfig[] = [
      {
        number: 1,
        title: t('webhookDialog.steps.configure.title'),
        description: t('webhookDialog.steps.configure.description'),
      },
    ];

    if (hasInputSpecs) {
      baseSteps.push({
        number: 2,
        title: t('webhookDialog.steps.mapInputs.title'),
        description: t('webhookDialog.steps.mapInputs.description'),
      });
    }

    return baseSteps;
  }, [hasInputSpecs, t]);

  /**
   * Handles navigation to the previous step
   */
  const handleBack = () => {
    if (currentStep > 1) {
      onStepChange(currentStep - 1);
    }
  };

  /**
   * Gets the current form data
   */
  const getFormData = useCallback(() => {
    return {
      selectedIntegration,
      selectedIntegrationVersion,
      selectedConnection,
      selectedEvent,
      triggerFieldGroups,
      inputMappings,
      configurationValues,
      filterConditions,
      // Derive filter capability from the event schema — same logic that
      // ConfigureStep uses to show the filter UI — so the update handler
      // doesn't need a second query and both decisions always agree.
      // The BDK `filter_capable` flag is intentionally not used here; capability
      // is determined purely by whether the trigger exposes filterable fields.
      filterCapable:
        extractFilterableFields(selectedTriggerDescriptor).length > 0,
    };
  }, [
    selectedIntegration,
    selectedIntegrationVersion,
    selectedConnection,
    selectedEvent,
    triggerFieldGroups,
    inputMappings,
    configurationValues,
    filterConditions,
    selectedTriggerDescriptor,
  ]);

  /**
   * Validates that every required configuration field has a value. Populates
   * per-field errors and returns `false` if anything is missing. Empty errors
   * are cleared on success so prior validation noise doesn't linger.
   */
  const validateConfiguration = useCallback((): boolean => {
    const missing = getMissingRequiredConfigFields(
      selectedTriggerDescriptor?.configuration,
      configurationValues,
    );
    if (missing.length === 0) {
      setConfigErrors({});
      return true;
    }
    const message = t('webhookDialog.configuration.errors.required');
    setConfigErrors(Object.fromEntries(missing.map(key => [key, message])));
    return false;
  }, [selectedTriggerDescriptor, configurationValues, t]);

  /**
   * Validates that every filter condition with a value-requiring operator has a
   * value. Populates per-condition errors and returns `false` if anything is
   * missing.
   */
  const validateFilters = useCallback((): boolean => {
    const invalid = getInvalidFilterConditionIds(filterConditions);
    if (invalid.length === 0) {
      setFilterErrors({});
      return true;
    }
    const message = t('webhookDialog.filters.errors.valueRequired');
    setFilterErrors(Object.fromEntries(invalid.map(id => [id, message])));
    return false;
  }, [filterConditions, t]);

  const connectionGate = useInactiveConnectionGate({
    selectedIntegration,
    selectedConnection,
    enabled: isOpen && mode !== 'readOnly',
  });
  const { isPreparingConnection, runWithActiveConnection } = connectionGate;

  /**
   * Advances to the next step, or saves/updates if on the last step
   */
  const proceed = () => {
    // If no inputSpecs, save directly from step 1
    if (!hasInputSpecs && currentStep === 1) {
      const formData = getFormData();

      if (mode === 'edit') {
        if (hasAnyChanges) {
          onUpdate?.(formData);
        } else {
          // No changes - just close
          onOpenChange(false);
        }
      } else {
        // Create mode
        onSave?.(formData);
      }
      return;
    }

    // Multi-step flow (has inputSpecs)
    if (currentStep < steps.length) {
      onStepChange(currentStep + 1);
    } else {
      // Last step - save or update based on mode
      const formData = getFormData();

      if (mode === 'edit') {
        if (hasAnyChanges) {
          onUpdate?.(formData);
        } else {
          // No changes - just close
          onOpenChange(false);
        }
      } else {
        // Create mode
        onSave?.(formData);
      }
    }
  };

  /**
   * Handles the primary action: validates, reactivates an inactive connection
   * when needed, then advances or saves
   */
  const handleNext = () => {
    // Run configuration + filter validation before any save / step-advance.
    // If we're past step 1 when validation fails, also jump back to step 1 so
    // the user can see the inline error. Both validators run so their errors
    // surface together.
    if (mode !== 'readOnly') {
      const configValid = validateConfiguration();
      const filtersValid = validateFilters();
      if (!configValid || !filtersValid) {
        if (currentStep !== 1) {
          onStepChange(1);
        }
        return;
      }
    }

    void runWithActiveConnection(proceed, { waitForReady: isLastStep });
  };

  /**
   * Handles dialog open state changes
   * Prevents closing when loading
   */
  const handleOpenChange = (open: boolean) => {
    if (isLoading) return;
    onOpenChange(open);
  };

  const isLastStep = currentStep === steps.length;
  const isFirstStep = currentStep === 1;
  const isReadOnlyMode = mode === 'readOnly';
  const isEditMode = mode === 'edit';

  /**
   * Renders the current step content
   */
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <ConfigureStep
            selectedIntegration={selectedIntegration}
            onIntegrationChange={onSelectedIntegrationChange}
            onIntegrationVersionChange={onSelectedIntegrationVersionChange}
            selectedConnection={selectedConnection}
            onConnectionChange={onSelectedConnectionChange}
            selectedEvent={selectedEvent}
            onEventChange={onSelectedEventChange}
            onTriggerFieldGroupsExtracted={onTriggerFieldGroupsChange}
            configurationValues={configurationValues}
            onConfigurationChange={handleConfigurationChange}
            readOnly={isReadOnlyMode || isPreparingConnection}
            hasNextStep={hasInputSpecs}
            lockSelections={
              isReadOnlyMode || isEditMode || isPreparingConnection
            }
            configErrors={configErrors}
            onSelectedTriggerChange={setSelectedTriggerDescriptor}
            onSelectedIntegrationDisplayNameChange={
              setSelectedIntegrationDisplayName
            }
            filterConditions={filterConditions}
            onFilterConditionsChange={handleFilterConditionsChange}
            filterErrors={filterErrors}
            highlightedConfigKey={configFill.token}
            highlightConfigNonce={configFill.nonce}
            highlightedFilterId={filterFill.token}
            highlightFilterNonce={filterFill.nonce}
          />
        );
      case 2:
        // Only render step 2 if there are inputSpecs
        if (!hasInputSpecs) {
          return null;
        }
        return (
          <MapInputsStep
            inputSpecs={inputSpecs}
            mappings={inputMappings}
            triggerFieldGroups={triggerFieldGroups}
            onMappingChange={handleMappingChange}
            readOnly={isReadOnlyMode || isPreparingConnection}
          />
        );
      default:
        return null;
    }
  };

  // Button label derived from utility function
  const defaultPrimaryButtonLabel = getPrimaryButtonLabel({
    mode,
    isLastStep,
    hasAnyChanges,
    hasConfigureChanges,
    hasInputSpecs,
    isLoading,
    translations: {
      updateTrigger: t('trigger.actions.updateTrigger'),
      done: t('trigger.actions.done'),
      saveAndNext: t('trigger.actions.saveAndNext'),
      next: t('trigger.actions.next'),
      saveTrigger: t('trigger.actions.saveTrigger'),
      saving: t('trigger.actions.saving'),
      updating: t('trigger.actions.updating'),
    },
  });

  const { label: primaryButtonLabel, showPowerIcon } = getConnectionAction(
    connectionGate,
    {
      waiting: t('trigger.actions.waitingForConnection'),
      reactivate: t(
        isLastStep
          ? 'trigger.actions.reactivateAndSave'
          : 'trigger.actions.reactivateAndContinue',
      ),
      default: defaultPrimaryButtonLabel,
    },
  );

  // Button variant derived from utility function
  const primaryButtonVariant = getPrimaryButtonVariant({
    mode,
    isLastStep,
    hasAnyChanges,
    hasConfigureChanges,
  });

  // Dialog title derived from utility function
  const dialogTitle = getDialogTitle({
    mode,
    triggerName,
    translations: {
      viewTitle: t('webhookDialog.viewTitle', { triggerName }),
      editTitle: t('webhookDialog.editTitle', { triggerName }),
      createTitle: t('webhookDialog.title'),
    },
  });

  // The dialog body (header + stepper + step content). Rendered as the plain
  // direct children of DialogContent when there's no assistant (identical to
  // the stock dialog), or hosted inside the assistant layout wrapper when
  // `canAssist` is true.
  const dialogBody = (
    <>
      {/* Header */}
      <DialogHeader className="gap-1">
        <Flex align="center" gap={2}>
          <DialogTitle className="text-xl font-medium">
            {dialogTitle}
          </DialogTitle>
          {triggerStatus && <TriggerStatusBadge status={triggerStatus} />}
        </Flex>
        <DialogDescription className="text-sm">
          {hasInputSpecs
            ? t('webhookDialog.description')
            : t('webhookDialog.noInputsDescription')}
        </DialogDescription>
      </DialogHeader>

      {/* Stepper - only show if there are multiple steps */}
      {hasInputSpecs && (
        <DialogStepper steps={steps} currentStep={currentStep} />
      )}

      {/* Step Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {renderStepContent()}
      </div>
    </>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={`p-5 gap-5 ${
          canAssist
            ? TRIGGER_DIALOG_MAX_WIDTH_CLASS.withAssistant
            : TRIGGER_DIALOG_MAX_WIDTH_CLASS.default
        } max-h-[calc(100vh-100px)] flex flex-col transition-[max-width] duration-300`}
        showCloseButton={!isLoading}
      >
        <TriggerAssistantDialogLayout
          panel={
            canAssist ? (
              <IntegrationAssistantPanel
                integrationName={selectedIntegration ?? ''}
                integrationDisplayName={
                  selectedIntegrationDisplayName ?? selectedIntegration ?? ''
                }
                integrationVersion={selectedIntegrationVersion ?? ''}
                organizationId={organizationId}
                workspaceId={workspaceId}
                selectedAuthId={undefined}
                connectionId={selectedConnection}
                mode="trigger"
                triggerId={selectedEvent}
                onFillForm={
                  isReadOnlyMode ? undefined : handleAssistantFillForm
                }
                onAddFilter={isReadOnlyMode ? undefined : handleAddFilter}
                onRemoveFilter={isReadOnlyMode ? undefined : handleRemoveFilter}
                onClearFilters={isReadOnlyMode ? undefined : handleClearFilters}
                isOpen
                side="left"
                closable={false}
              />
            ) : null
          }
        >
          {dialogBody}
        </TriggerAssistantDialogLayout>

        {/* Footer */}
        <DialogFooter
          className={
            onToggleActiveState || onDelete
              ? 'sm:justify-between'
              : 'sm:justify-end'
          }
        >
          {(onToggleActiveState || onDelete) && (
            <Flex gap={2}>
              {onToggleActiveState && (
                <Button
                  variant="secondary"
                  onClick={onToggleActiveState}
                  disabled={
                    isToggleActiveStateLoading ||
                    triggerStatus === TRIGGER_STATUS.ERROR ||
                    triggerStatus === TRIGGER_STATUS.PENDING
                  }
                  isLoading={isToggleActiveStateLoading}
                >
                  {triggerStatus === TRIGGER_STATUS.ACTIVE ? (
                    <Icon size="md" type="Pause" />
                  ) : (
                    <Icon size="md" type="Play" />
                  )}
                  {triggerStatus === TRIGGER_STATUS.ACTIVE
                    ? t('trigger.actions.pause')
                    : t('trigger.actions.resume')}
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="destructive"
                  onClick={onDelete}
                  disabled={isToggleActiveStateLoading}
                >
                  <Icon size="md" type="Trash" />
                  {t('trigger.actions.delete')}
                </Button>
              )}
            </Flex>
          )}
          {isReadOnlyMode ? (
            /* Read-only mode - just show navigation and Close button */
            <Flex gap={2}>
              <Button
                variant="secondary"
                onClick={handleBack}
                disabled={isFirstStep}
              >
                {t('trigger.actions.back')}
              </Button>
              {isLastStep ? (
                <Button variant="default" onClick={() => onOpenChange(false)}>
                  {t('trigger.actions.close')}
                </Button>
              ) : (
                <Button variant="default" onClick={handleNext}>
                  {t('trigger.actions.next')}
                </Button>
              )}
            </Flex>
          ) : (
            /* Create/Edit mode - Back and Primary buttons */
            <Flex gap={2}>
              <Button
                variant="secondary"
                onClick={handleBack}
                disabled={isFirstStep || isLoading}
              >
                {t('trigger.actions.back')}
              </Button>
              <Button
                variant={primaryButtonVariant}
                onClick={handleNext}
                disabled={isLoading || isPreparingConnection}
                isLoading={isLoading || isPreparingConnection}
              >
                {showPowerIcon && <Icon size="md" type="Power" />}
                {primaryButtonLabel}
              </Button>
            </Flex>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
